import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import { fmtDate } from "@/lib/api";
import { NotesTab, LocationTab, PlansTab, LogsTab, CheckinsTab } from "@/pages/coach/ClientTabsA";
import { PhotosTab, HabitsTab, RemindersTab, SummaryTab, ClientBillingTab } from "@/pages/coach/ClientTabsB";

const TABS = [
  ["plans", "Plans", PlansTab], ["logs", "Logs", LogsTab],
  ["checkins", "Check-ins", CheckinsTab], ["photos", "Photos", PhotosTab], ["habits", "Habits", HabitsTab],
  ["reminders", "Reminders", RemindersTab], ["summary", "AI Summary", SummaryTab], ["billing", "Payments", ClientBillingTab],
  ["notes", "Notes", NotesTab], ["location", "Location", LocationTab],
];

function formatTimezone(timezone) {
  if (!timezone) return "";
  try {
    return new Intl.DateTimeFormat("en-IN", { timeZone: timezone, timeZoneName: "short" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value || "GMT";
  } catch {
    return timezone;
  }
}

export default function ClientDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [client, reload] = useLoad(`/clients/${id}`);
  const requestedTab = params.get("tab");
  const tab = TABS.some(([key]) => key === requestedTab) ? requestedTab : "plans";
  if (!client || Array.isArray(client)) return <div className="eyebrow">Loading client…</div>;
  const location = [client.city, client.state, client.country].filter(Boolean).join(", ");
  const locationDetails = [location, formatTimezone(client.timezone)].filter(Boolean).join(" · ") || "-";
  return (
    <div>
      <PageHeader eyebrow={`${fmtDate(client.start_date)} - ${fmtDate(client.end_date)}`} title={
        <span className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <span>{client.name}</span>
          <span className="flex flex-col items-start gap-2">
            <StatusBadge status={client.status} testid="client-membership-status" />
            <span className="text-sm font-medium normal-case tracking-normal text-slate-400">{client.package_name || "No plan"}{client.duration_weeks ? ` · ${client.duration_weeks} weeks` : ""}</span>
          </span>
        </span>
      } testid="client-detail-name" subtitle={<div className="mt-1 text-sm text-slate-400 break-words">{locationDetails}</div>} leading={
        <Button asChild type="button" variant="ghost" size="sm" data-testid="client-detail-back-btn">
          <Link to="/coach/clients" aria-label="Back to clients" title="Back to clients"><ArrowLeft size={16} className="mr-1" />Clients</Link>
        </Button>
      } details={
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-3 text-sm" data-testid="client-crm-record">
          <div><div className="eyebrow">Phone</div><div className="mt-1">{client.phone || "-"}</div></div>
          <div className="col-span-2 sm:col-span-2 xl:col-span-2 grid grid-cols-2 gap-x-6">
            <div><div className="eyebrow">WhatsApp</div><div className="mt-1 break-words">{client.whatsapp_number || "-"}</div></div>
            <div><div className="eyebrow">Email</div><div className="mt-1 break-words">{client.email || "-"}</div></div>
          </div>
          <div><div className="eyebrow">Coach</div><div className="mt-1">{client.coach_name || "Unassigned"}</div></div>
          <div><div className="eyebrow">Login</div><div className="mt-1" data-testid="client-login-status">{client.login || "-"}</div></div>
        </div>
      } />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-[#111827] border border-[#1E293B] flex-wrap h-auto p-1 mb-6">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} className="rounded-full data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white" data-testid={`client-tab-${k}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([k, , C]) => (
          <TabsContent key={k} value={k}>{tab === k && <C client={client} reloadClient={reload} />}</TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
